import { Navigate } from 'react-router';
import type { User } from '@bekebi/contracts/api';
import { Button } from '../../components/button';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { useCurrentUser, useHasPermission } from '../../session/current-user';
import { useServices } from '../services/api';
import { EntryRow, type Lookups } from './activity-overview';
import { useActivity } from './api';

/**
 * My Activity (/app/my-activity, `audit.view_own`). For ICT the existing GET /api/activity returns
 * only entries where they are the actor (OD-3), so this list is theirs by the API's own rule — the
 * browser does not filter or add anything. Entries use the Activity page's own row (time, actor,
 * action with its code, what it concerned, recorded details as returned, redactions kept), with
 * the signed-in user shown as "You". Older entries load from the API's own cursor.
 *
 * A role that sees every entry (`audit.view`, the Admin) is sent to the full Activity page,
 * because that list would not be "my" activity.
 */
export function MyActivityPage() {
  const seesEveryone = useHasPermission('audit.view');
  if (seesEveryone) return <Navigate to="/app/activity" replace />;
  return <MyActivityList />;
}

/**
 * Names for the targets of the signed-in user's own entries: themselves (from the session — no
 * user list is read) and the services they may view. ICT has no infrastructure access, so no
 * resource names are looked up.
 */
export function useOwnActivityLookups(): Lookups {
  const me = useCurrentUser();
  const services = useServices();
  const self: User = {
    id: me.id,
    bekebiUserId: me.bekebiUserId,
    email: me.email,
    role: me.role,
    status: 'active',
    passwordSet: true,
    accountLink: null,
    lastLoginAt: null,
    createdAt: '',
    updatedAt: '',
  };
  return {
    users: new Map([[me.id, self]]),
    services: new Map((services.data ?? []).map((s) => [s.id, s])),
    resources: new Map(),
  };
}

function MyActivityList() {
  const me = useCurrentUser();
  const activity = useActivity();
  const lookups = useOwnActivityLookups();
  const entries = activity.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="max-w-6xl">
      <h1>My Activity</h1>
      <p className="mt-1 text-sm text-muted">
        What you have done in the portal, newest first, as the portal recorded it. Entries cannot be
        changed or removed.
      </p>
      <div className="mt-6">
        {activity.isPending ? (
          <LoadingState label="Loading your activity…" />
        ) : activity.isError && entries.length === 0 ? (
          <ErrorState
            title="Your activity could not be loaded"
            message={activity.error.message}
            onRetry={() => void activity.refetch()}
          />
        ) : entries.length === 0 ? (
          <EmptyState
            title="No activity recorded yet."
            description="Actions you take in the portal will appear here."
          />
        ) : (
          <>
            <div
              aria-hidden="true"
              className="hidden grid-cols-[1fr_1.3fr_1.2fr_1.3fr] gap-4 px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted md:grid"
            >
              <span>Time</span>
              <span>Actor</span>
              <span>Action</span>
              <span>Concerning</span>
            </div>
            <ol aria-label="My activity" className="space-y-2">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <EntryRow entry={entry} lookups={lookups} selfId={me.id} />
                </li>
              ))}
            </ol>
            <div className="mt-4">
              {activity.isFetchNextPageError ? (
                <p role="alert" className="mb-3 text-sm text-danger">
                  Older activity could not be loaded: {activity.error?.message}
                </p>
              ) : null}
              {activity.hasNextPage ? (
                <Button
                  variant="secondary"
                  onClick={() => void activity.fetchNextPage()}
                  disabled={activity.isFetchingNextPage}
                >
                  {activity.isFetchingNextPage
                    ? 'Loading…'
                    : activity.isFetchNextPageError
                      ? 'Try again'
                      : 'Show older activity'}
                </Button>
              ) : (
                <p className="text-sm text-muted">No older activity.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
